import {
  DeltaStreamCallbacks,
  StreamedMessageResponse,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import {
  MainResponse,
  ResourcesContent,
  SolutionSessionMessage,
  CollectedData,
  LubavSessionType,
  SolutionSessionMessageType,
  ResumeBuilderStep,
} from '@backrs_ui/grow-ui/src/models/models';
import { createDeltaStreamHandler } from '@backrs_ui/grow-ui/src/utils/streamUtils';

import { createApi } from '@reduxjs/toolkit/query/react';
import { jsonrepair } from 'jsonrepair';

import { loadSessionData } from './solutionSession.thunk';
import { lubavAppBaseQuery } from '../baseQuery';
import { RootState } from '../store';

type GetResponseRequest = Readonly<{
  session_id: number;
  llm: string;
  solution_session: string;
  with_final_message?: boolean;
  onChunk?: (chunk: string) => void;
  signal?: AbortSignal;
}>;

type GetSessionDataRequest = Readonly<{
  session_id: number;
}>;

type GetSessionDataResponse = Readonly<{
  session_id: number;
  session_type: LubavSessionType;
  messages: SolutionSessionMessage[];
  main_response: MainResponse | null;
  collected_data: CollectedData;
  additional_resources: ResourcesContent;
  finished: boolean;
}>;

type GetResponseResponse = Readonly<{
  output: Record<string, MainResponse>;
}>;

type GetMostRecentSessionRequest = Readonly<{
  solution_session: string;
}>;

type GetMostRecentResumeBuilderSubsessionsResponse = Readonly<{
  subsessions: Record<string, GetSessionDataResponse>;
}>;

type SendChatMessageRequest = Readonly<{
  text: string;
  session_id: number;
  llm: string;
  solution_session: string;
  hidden_message?: boolean;
  signal?: AbortSignal;
}> &
  DeltaStreamCallbacks;

type GetNewSessionIdForMessageEditRequest = Readonly<{
  session_id: number;
  query_id: string;
}>;

type GetNewSessionIdForMessageEditResponse = Readonly<{
  session_id: number;
}>;

type SendQAMessageRequest = Readonly<{
  text: string;
  session_id: number;
  question_key?: string;
  llm: string;
  solution_session: string;
}>;

type SendQAMessageResponse = Readonly<{
  text: string;
  query_id: string;
  response_id: string;
  finished: boolean;
  validation_feedback?: string;
  response_is_valid?: boolean;
  question?: {
    text?: string;
    field?: string;
    type?: SolutionSessionMessageType;
    options?: string[];
    include_freeform?: boolean;
    required?: boolean;
    placeholder?: string;
    update_on_submit?: boolean;
    resume_builder_step?: ResumeBuilderStep;
    additional_text?: string;
  };
}>;

type GetCollectedDataRequest = Readonly<{
  session_id: number;
}>;

export const solutionSessionApi = createApi({
  reducerPath: 'solutionSessionApi',
  baseQuery: lubavAppBaseQuery,
  tagTypes: ['SessionData'],
  endpoints: (build) => ({
    getSessionData: build.query<GetSessionDataResponse, GetSessionDataRequest>({
      query: ({ session_id }) => ({
        url: '/api/solution-session/get-session-data',
        method: 'POST',
        body: session_id,
      }),
      providesTags: ['SessionData'],
    }),
    getResponse: build.mutation<GetResponseResponse, GetResponseRequest>({
      query: ({ onChunk, signal, ...body }) => ({
        url: '/api/solution-session/get-response',
        method: 'POST',
        body,
        signal,
        responseHandler: async (response) => {
          const reader = response.body?.getReader();

          if (!reader) return null;

          let result = '';

          try {
            while (true) {
              if (signal?.aborted) {
                break;
              }

              const { done, value } = await reader.read();

              if (done) break;

              const chunk = new TextDecoder().decode(value);

              // remove "data: "
              result += chunk.replace(/(^|\n)data:\s*/g, '');

              onChunk?.(result);
            }
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              return result;
            }

            throw error;
          } finally {
            reader.releaseLock();
          }

          return result;
        },
      }),
      transformResponse: (response: string) => {
        try {
          const fixedJson = jsonrepair(response);
          const parsedResponse = JSON.parse(fixedJson) as GetResponseResponse;

          return parsedResponse;
        } catch (error) {
          console.error('Failed to repair JSON response:', error);

          return { output: {} };
        }
      },
    }),
    getResumeTemplate: build.query<string, string>({
      query: (resume_section) => ({
        url: '/api/solution-session/get-resume-template',
        method: 'POST',
        body: { resume_section },
      }),
    }),
    getMostRecentSession: build.query<GetSessionDataResponse | null, GetMostRecentSessionRequest>({
      query: ({ solution_session }) => ({
        url: '/api/solution-session/get-most-recent-session',
        method: 'POST',
        body: { solution_session },
      }),
      providesTags: ['SessionData'],
      // dispatch loadSessionData to load into redux
      onQueryStarted: async ({ solution_session }, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;

          if (data) {
            const state = getState() as RootState;
            const sessionState = state.sessionState.sessions[solution_session];

            const isSessionEmpty =
              !sessionState?.coachLubav?.sessionId &&
              !sessionState?.boxData?.boxData?.mainResponse &&
              !sessionState?.boxData?.boxData?.collectedData;

            if (isSessionEmpty) {
              dispatch(loadSessionData({ sessionData: data, activeSession: solution_session }));
            }
          }
        } catch (error) {
          console.error('Failed to load most recent session data:', error);
        }
      },
    }),
    getMostRecentResumeBuilderSubsessions: build.query<
      GetMostRecentResumeBuilderSubsessionsResponse,
      undefined
    >({
      queryFn: async (_, __, ___, baseQuery) => {
        const resumeBuilderSubsessions = [
          'resume_builder.education',
          'resume_builder.work_experience',
          'resume_builder.leadership',
          'resume_builder.skills',
        ];

        const subsessionData: Record<string, GetSessionDataResponse> = {};

        // fetch each subsession data
        const promises = resumeBuilderSubsessions.map(async (subsession) => {
          const result = await baseQuery({
            url: '/api/solution-session/get-most-recent-session',
            method: 'POST',
            body: { solution_session: subsession },
          });

          if (result.data) {
            const sessionData = result.data as GetSessionDataResponse;

            subsessionData[subsession] = sessionData;
          }
        });

        await Promise.all(promises);

        return {
          data: {
            subsessions: subsessionData,
          },
        };
      },
      providesTags: ['SessionData'],
      // dispatch loadSessionData for each subsession to load into redux
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          Object.entries(data.subsessions).forEach(([subsessionType, sessionData]) => {
            dispatch(loadSessionData({ sessionData, activeSession: subsessionType }));
          });
        } catch (error) {
          console.error('Failed to load most recent session data:', error);
        }
      },
    }),
    sendChatMessage: build.mutation<StreamedMessageResponse, SendChatMessageRequest>({
      query: ({ onMeta, onDelta, onTool, onDone, signal, ...body }) => ({
        url: '/api/solution-session/send-chat-message',
        method: 'POST',
        body,
        signal,
        responseHandler: createDeltaStreamHandler({ onMeta, onDelta, onTool, onDone }),
      }),
    }),
    getNewSessionIdForMessageEdit: build.mutation<
      GetNewSessionIdForMessageEditResponse,
      GetNewSessionIdForMessageEditRequest
    >({
      query: (body) => ({
        url: '/api/solution-session/get-new-session-id-for-message-edit',
        method: 'POST',
        body,
      }),
    }),
    sendQAMessage: build.mutation<SendQAMessageResponse, SendQAMessageRequest>({
      query: (body) => ({
        url: '/api/solution-session/send-qa-message',
        method: 'POST',
        body,
      }),
    }),
    getCollectedData: build.mutation<CollectedData, GetCollectedDataRequest>({
      query: ({ session_id }) => ({
        url: '/api/solution-session/get-collected-data',
        method: 'POST',
        body: session_id,
      }),
    }),
  }),
});

export const {
  useGetResponseMutation,
  useGetResumeTemplateQuery,
  useGetSessionDataQuery,
  useGetMostRecentSessionQuery,
  useGetMostRecentResumeBuilderSubsessionsQuery,
  useSendChatMessageMutation,
  useGetNewSessionIdForMessageEditMutation,
  useSendQAMessageMutation,
  useGetCollectedDataMutation,
  util: { resetApiState },
} = solutionSessionApi;
